import { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { trackClientEvent } from 'analytics/analytics'

const FAILED_RESPONSE_BANNER_EVENT = {
  subject: 'le_chat_failed_response_banner',
  category: 'trip_planner',
} as const

interface Props {
  messageId: string
  active: boolean
  hasRetried: boolean
  isStreaming: boolean
  onRetry: () => void
  onContinueInNewChat: () => void
}

export default function FailedMessageBanner({ messageId, active, hasRetried, isStreaming, onRetry, onContinueInNewChat }: Props) {
  const intl = useIntl()
  const lastDisplayedKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!active || isStreaming) return
    const displayKey = `${messageId}:${hasRetried}`
    if (lastDisplayedKeyRef.current === displayKey) return
    lastDisplayedKeyRef.current = displayKey
    trackClientEvent({
      ...FAILED_RESPONSE_BANNER_EVENT,
      action: 'banner_displayed',
      type: 'nonInteraction',
    })
  }, [active, hasRetried, isStreaming, messageId])

  const handleRetry = () => {
    trackClientEvent({
      ...FAILED_RESPONSE_BANNER_EVENT,
      action: 'clicked_retry',
      type: 'interaction',
    })
    onRetry()
  }

  const handleContinueInNewChat = () => {
    trackClientEvent({
      ...FAILED_RESPONSE_BANNER_EVENT,
      action: 'clicked_try_in_new_chat',
      type: 'interaction',
    })
    onContinueInNewChat()
  }
  const heading = intl.formatMessage({
    id: 'yXhuv-m0Qmnx1cYfPPha',
    defaultMessage: 'Something went wrong',
    description: 'Heading shown in the chat when an entire assistant response fails to complete',
  })
  const retryLabel = intl.formatMessage({
    id: 'On30ejxYGhasjuF3Bi3T',
    defaultMessage: 'Try again',
    description: 'Button that starts a fresh generation for the latest failed assistant response',
  })
  const newChatLabel = intl.formatMessage({
    id: 'DUjJe3jwn2X_ldmjsDfE',
    defaultMessage: 'Continue in a new chat',
    description: 'Button that starts a new trip-planning chat after the assistant response fails again',
  })
  const description = intl.formatMessage(hasRetried ? {
    id: 'V8lcWyOSnHzfJ2bYtgmX',
    defaultMessage: 'It still isn’t working. You can pick up where you left off in a new chat.',
    description: 'Explains the recovery option after a second whole-response failure in trip planner chat',
  } : {
    id: 'wM8sZRWW0KPR8fmMC7HQ',
    defaultMessage: 'Your chat is saved. Give it another try.',
    description: 'Explains that an assistant response failed while the existing trip remains available',
  })

  return <MessageBanner
    kind="warning"
    heading={heading}
    primaryActionKind="primary"
    primaryActionButtonProps={active ? {
      children: hasRetried ? newChatLabel : retryLabel,
      onClick: hasRetried ? handleContinueInNewChat : handleRetry,
      disabled: isStreaming,
    } : undefined}
  >
    {description}
  </MessageBanner>
}
